// Project Service - Connects to Magnus-Core backend API
// Fetches projects and manages PFMEA-specific data
// Uses Vite proxy for local development (see vite.config.ts)
// ============================================================

// import { auth } from '../config/firebase'; // Removed unused import

export interface ProjectMetadata {
    id: string;
    name: string;
    displayName: string;
    customer: string;
    status: 'active' | 'inactive' | 'archived';
    stage: 'concept' | 'design' | 'prototype' | 'production' | 'maintenance';
    owner: string;
    lastModified: string;
    created: string;
    fmeaStandard: string;
    itemCount: number;
    pfmeaCount?: number;
    processFlowCount?: number;
    controlPlanCount?: number;
}

export interface PfmeaProject {
    metadata: ProjectMetadata;
    processFlow?: any;
    pfmeaItems?: any[];
    controlPlan?: any;
}

import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

// Removed local authenticatedFetch and getAuthToken implementation in favor of shared utility
// that handles environment-aware base URLs correctly.

// Fetch list of projects from backend
export async function fetchProjects(): Promise<ProjectMetadata[]> {
    try {
        const response = await authenticatedFetchJSON<any[]>('/api/projects/');

        // Transform to ProjectMetadata format
        return response.map((project: any) => ({
            id: project.project_name || project.name,
            name: project.project_name || project.name,
            displayName: project.display_name || project.project_name || project.name,
            customer: project.customer || 'Unknown',
            status: project.status || 'active',
            stage: project.stage || 'design',
            owner: project.owner || 'system',
            lastModified: project.updated_at || project.lastModified || new Date().toISOString(),
            created: project.created_at || project.created || new Date().toISOString(),
            fmeaStandard: project.fmea_standard || project.fmeaStandard || 'AIAG_VDA',
            itemCount: project.item_count || project.itemCount || 0,
            pfmeaCount: project.pfmea_count || 0,
            processFlowCount: project.process_flow_count || 0,
            controlPlanCount: project.control_plan_count || 0,
        }));
    } catch (error) {
        console.error('Error fetching projects:', error);
        return [];
    }
}

// Load a specific project with PFMEA data
export async function loadProject(projectName: string): Promise<PfmeaProject | null> {
    try {
        const encodedName = encodeURIComponent(projectName);
        const data = await authenticatedFetchJSON<any>(`/api/projects/load/${encodedName}`);

        return {
            metadata: {
                id: data.projectMetadata?.name || projectName,
                name: data.projectMetadata?.name || projectName,
                displayName: data.projectMetadata?.displayName || projectName,
                customer: data.projectMetadata?.customer || 'Unknown',
                status: data.projectMetadata?.status || 'active',
                stage: data.projectMetadata?.stage || 'design',
                owner: data.projectMetadata?.owner || 'system',
                lastModified: data.projectMetadata?.lastModified || new Date().toISOString(),
                created: data.projectMetadata?.created || new Date().toISOString(),
                fmeaStandard: data.projectMetadata?.fmeaStandard || 'AIAG_VDA',
                itemCount: data.fmeaItems?.length || 0,
            },
            processFlow: data.processFlow || null,
            pfmeaItems: data.fmeaItems || [],
            controlPlan: data.controlPlan || null,
        };
    } catch (error) {
        console.error('Error loading project:', error);
        return null;
    }
}

// Save PFMEA data to a project
export async function savePfmeaToProject(
    projectName: string,
    pfmeaData: { processFlow?: any; pfmeaItems?: any[]; controlPlan?: any },
    existingMetadata?: Partial<ProjectMetadata>
): Promise<boolean> {
    try {
        // Build the payload in the format expected by the backend
        const payload = {
            projectMetadata: {
                id: existingMetadata?.id || projectName.replace(/\s+/g, '_'),
                name: projectName.replace(/\s+/g, '_'),
                displayName: existingMetadata?.displayName || projectName,
                customer: existingMetadata?.customer || 'Unknown',
                status: existingMetadata?.status || 'active',
                stage: existingMetadata?.stage || 'design',
                fmeaStandard: existingMetadata?.fmeaStandard || 'AIAG_VDA',
                lastModified: new Date().toISOString(),
            },
            fmeaItems: pfmeaData.pfmeaItems || [],
            processFlow: pfmeaData.processFlow || null,
            controlPlan: pfmeaData.controlPlan || null,
        };

        await authenticatedFetchJSON('/api/projects/save', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        return true;
    } catch (error) {
        console.error('Error saving PFMEA data:', error);
        return false;
    }
}

// Check if project has PFMEA data
export function hasPfmeaData(project: PfmeaProject): boolean {
    return (
        (project.processFlow?.steps?.length > 0) ||
        (project.pfmeaItems && project.pfmeaItems.length > 0) ||
        (project.controlPlan?.items?.length > 0)
    );
}

// ============================================================
// 5T PROJECT SCOPING (Firebase Integration)
// ============================================================

export interface ProjectScopingData {
    intent: {
        goals: string[];
        scopeBoundaries: string;
        assumptions: string[];
        exclusions?: string[];
    };
    timing: {
        kickoffDate: string;
        targetCompletionDate: string;
        milestones: Array<{
            name: string;
            date: string;
            status: 'pending' | 'in-progress' | 'completed';
        }>;
    };
    team: {
        lead: string;
        members: Array<{
            name: string;
            role: string;
            department: string;
            expertise: string;
        }>;
    };
    tools: {
        software: string[];
        templates: string[];
        standards: string[];
        references: string[];
    };
    tasks: Array<{
        id: string;
        name: string;
        owner: string;
        dueDate: string;
        status: 'not-started' | 'in-progress' | 'completed' | 'blocked';
        dependencies?: string[];
    }>;
}

// Load 5T Scoping from Firebase
export async function loadProjectScoping(projectName: string): Promise<ProjectScopingData | null> {
    try {
        const encodedName = encodeURIComponent(projectName);
        const data = await authenticatedFetchJSON<any>(`/api/projects/${encodedName}/scoping`);
        return data.scoping || null;
    } catch (error) {
        console.warn('Project scoping not found, using defaults:', error);
        return null;
    }
}

// Save 5T Scoping to Firebase
export async function saveProjectScoping(
    projectName: string,
    scoping: ProjectScopingData
): Promise<boolean> {
    try {
        const encodedName = encodeURIComponent(projectName);
        await authenticatedFetchJSON(`/api/projects/${encodedName}/scoping`, {
            method: 'POST',
            body: JSON.stringify({ scoping }),
        });
        return true;
    } catch (error) {
        console.error('Error saving project scoping:', error);
        return false;
    }
}

// ============================================================
// PROCESS FLOW (Firebase Integration)
// ============================================================

export interface ProcessFlowData {
    nodes: any[];
    edges: any[];
    processItems?: any[];
    workElements?: any[];
}

// Load Process Flow from Firebase
export async function loadProcessFlow(projectName: string): Promise<ProcessFlowData | null> {
    try {
        const encodedName = encodeURIComponent(projectName);
        const data = await authenticatedFetchJSON<any>(`/api/projects/${encodedName}/process-flow`);
        return data || null;
    } catch (error) {
        console.warn('Process flow not found:', error);
        return null;
    }
}

// Save Process Flow to Firebase
export async function saveProcessFlow(
    projectName: string,
    flowData: ProcessFlowData
): Promise<boolean> {
    try {
        const encodedName = encodeURIComponent(projectName);
        await authenticatedFetchJSON(`/api/projects/${encodedName}/process-flow`, {
            method: 'POST',
            body: JSON.stringify(flowData),
        });
        return true;
    } catch (error) {
        console.error('Error saving process flow:', error);
        return false;
    }
}

export default {
    fetchProjects,
    loadProject,
    savePfmeaToProject,
    hasPfmeaData,
    loadProjectScoping,
    saveProjectScoping,
    loadProcessFlow,
    saveProcessFlow,
};
